import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  Clock,
  Heart,
  Newspaper,
} from "lucide-react";
import { API_URL } from "./Config/Env";
import Swal from "sweetalert2";
import WhatsAppButton from "./Components/WhatsAppButton";

export default function Home() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBlogs = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${API_URL}/get_blog_posts/`);

      const posts = response.data.posts || [];

      const enriched = posts.map((post) => ({
        ...post,
        author: post.name || "Nexindi Tech",
        date: post.created_at,
        avatar: post.profile_image || "/default-avatar.png",
        blogImage: post.image || "/images/blog-placeholder.jpg",
      }));

      setBlogs(enriched);
    } catch (error) {
      console.error("Failed to fetch blogs:", error);

      Swal.fire({
        icon: "error",
        title: "Unable to Load Articles",
        text: "We couldn't load the latest articles. Please try again later.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleLike = async (blog_id) => {
    const user_id = localStorage.getItem("user_id");

    if (!user_id) {
      Swal.fire({
        icon: "warning",
        title: "Sign In Required",
        text: "Please sign in to like this article.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/like_blog_post/${user_id}/${blog_id}/`
      );

      if (response.status === 200) {
        fetchBlogs();
      }
    } catch (error) {
      console.error("Error liking blog:", error);

      Swal.fire({
        icon: "error",
        title: "Action Failed",
        text: "We couldn't update your like. Please try again.",
        confirmButtonColor: "#2563EB",
      });
    }
  };

  const formatDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleDateString("en-KE", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-hidden">
      {/* Hero */}
      <section className="relative bg-slate-950 text-white pt-28 pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[150px]" />

        <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[140px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-7">
            <Newspaper size={15} />

            Nexindi Insights
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight mb-7">
            Ideas, technology and
            <span className="block text-blue-400">
              insights for a digital future.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
            Explore insights on software, artificial intelligence, business
            technology, digital transformation, and the innovations shaping
            Africa's future.
          </p>
        </div>
      </section>

      {/* Blog Section */}
      <section className="relative py-24 px-6 bg-slate-50 overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-blue-100/60 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-7xl mx-auto">
          {/* Heading */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-sm font-semibold mb-5">
                <span className="w-2 h-2 bg-blue-600 rounded-full" />
                Latest Articles
              </div>

              <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
                Insights from
                <span className="text-blue-600"> Nexindi Tech.</span>
              </h2>
            </div>

            <p className="text-slate-600 text-lg max-w-md leading-relaxed">
              Practical perspectives on software development, emerging
              technology, digital products, and modern business.
            </p>
          </div>

          {/* Loading */}
          {loading && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <div
                  key={item}
                  className="bg-white rounded-3xl border border-slate-200 overflow-hidden animate-pulse"
                >
                  <div className="h-60 bg-slate-200" />

                  <div className="p-7">
                    <div className="h-4 w-24 bg-slate-200 rounded mb-5" />
                    <div className="h-7 bg-slate-200 rounded mb-3" />
                    <div className="h-7 w-2/3 bg-slate-200 rounded mb-6" />

                    <div className="space-y-2">
                      <div className="h-3 bg-slate-200 rounded" />
                      <div className="h-3 bg-slate-200 rounded" />
                      <div className="h-3 w-3/4 bg-slate-200 rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && blogs.length === 0 && (
            <div className="max-w-xl mx-auto py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-6">
                <Newspaper size={28} />
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                No articles yet
              </h3>

              <p className="text-slate-600 leading-relaxed">
                We're preparing new technology insights and stories. Check back
                soon for the latest updates from Nexindi Tech.
              </p>
            </div>
          )}

          {/* Blog Grid */}
          {!loading && blogs.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              {blogs.map((blog) => (
                <article
                  key={blog.id}
                  className="group flex flex-col bg-white rounded-3xl border border-slate-200 overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-900/10"
                >
                  {/* Image */}
                  <Link
                    to={`/blog/${blog.id}`}
                    className="relative h-60 overflow-hidden"
                  >
                    <img
                      src={blog.blogImage}
                      alt={blog.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/30 via-transparent to-transparent" />

                    <div className="absolute top-5 left-5">
                      <span className="px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-md text-xs font-semibold text-blue-600">
                        Technology
                      </span>
                    </div>
                  </Link>

                  {/* Content */}
                  <div className="flex flex-col flex-1 p-7">
                    {/* Author */}
                    <div className="flex items-center gap-3 mb-6">
                      <img
                        src={blog.avatar}
                        alt={blog.author}
                        className="w-10 h-10 rounded-xl object-cover border border-slate-200"
                      />

                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {blog.author}
                        </p>

                        <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                          <Clock size={12} />
                          {formatDate(blog.date)}
                        </div>
                      </div>
                    </div>

                    {/* Title */}
                    <Link to={`/blog/${blog.id}`}>
                      <h2 className="text-xl md:text-2xl font-bold text-slate-900 leading-snug mb-4 group-hover:text-blue-600 transition-colors">
                        {blog.title}
                      </h2>
                    </Link>

                    {/* Content Preview */}
                    <p className="text-slate-600 text-sm leading-relaxed mb-7">
                      {blog.content?.length > 180
                        ? `${blog.content.slice(0, 180)}...`
                        : blog.content}
                    </p>

                    {/* Bottom */}
                    <div className="mt-auto pt-5 border-t border-slate-200 flex items-center justify-between gap-4">
                      <button
                        onClick={() => handleLike(blog.id)}
                        className="group/like inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600 transition-colors"
                      >
                        <span className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center group-hover/like:bg-blue-50 transition">
                          <Heart size={15} />
                        </span>

                        <span>
                          {blog.likes_count || 0}
                        </span>
                      </button>

                      <Link
                        to={`/blog/${blog.id}`}
                        className="group/read inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700"
                      >
                        Read Article

                        <ArrowRight
                          size={16}
                          className="transition-transform group-hover/read:translate-x-1"
                        />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 bg-white">
        <div className="relative max-w-7xl mx-auto rounded-[2rem] bg-gradient-to-br from-blue-600 via-blue-700 to-slate-950 px-7 py-16 md:px-16 md:py-20 overflow-hidden text-white">
          <div className="absolute -right-20 -top-28 w-[400px] h-[400px] bg-cyan-300/15 rounded-full blur-[120px]" />

          <div className="relative z-10 grid lg:grid-cols-[1fr_auto] items-center gap-10">
            <div className="max-w-3xl">
              <p className="text-blue-200 text-sm font-semibold uppercase tracking-wider mb-4">
                Build With Nexindi
              </p>

              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-5">
                Need technology for your next idea?
              </h2>

              <p className="text-blue-100 text-lg leading-relaxed">
                Beyond sharing technology insights, we design and build
                websites, mobile applications, business systems, and intelligent
                digital solutions.
              </p>
            </div>

            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-3 bg-white text-blue-700 px-7 py-4 rounded-xl font-bold hover:bg-blue-50 transition-all duration-300 hover:-translate-y-1"
            >
              Start a Project

              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </section>

      <WhatsAppButton />
    </div>
  );
}